
import React, { createContext, useState, useEffect, useMemo, useCallback, ReactNode, useRef } from 'react';
import { CartItem, QuantityPricePair, PersistedFileInfo } from '../types';
import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { uploadFile } from '../utils/storageUtils';
import { trackAddToCart } from '../utils/analyticsEvents';
import { User } from 'firebase/auth';

export interface CartContextType {
    cartItems: CartItem[];
    addToCart: (item: CartItem, userId: string) => Promise<void>;
    removeFromCart: (itemId: string) => void;
    updateItemQuantity: (itemId: string, newPriceInfo: QuantityPricePair) => void;
    toggleItemUrgency: (itemId: string) => void;
    setUrgencyForAllItems: (isActive: boolean) => void;
    clearLocalCart: () => void;
    totalItems: number;
    cartTotal: number;
    isCartLoading: boolean;
}

export const CartContext = createContext<CartContextType | undefined>(undefined);

/**
 * Prepares cart items for Firestore by removing non-serializable data (`files`)
 * and ensuring no `undefined` values are present, which Firestore doesn't support.
 */
const serializeCartItems = (items: CartItem[]): any[] => {
    return items.map(item => {
        const { designInfo, ...restOfItem } = item;
        let serializableDesignInfo = null;

        if (designInfo) {
            // Explicitly remove the 'files' property which contains non-serializable File objects
            const { files, ...restOfDesignInfo } = designInfo;
            serializableDesignInfo = restOfDesignInfo;
        }

        const serializableItem = {
            ...restOfItem,
            designInfo: serializableDesignInfo,
        };

        // The JSON stringify/parse trick is a robust way to deep-clone the object
        // and automatically remove any fields with `undefined` values.
        return JSON.parse(JSON.stringify(serializableItem));
    });
};


const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [cartItems, setCartItems] = useState<CartItem[]>([]);
    const { user, loading: authLoading } = useAuth();
    const [isCartLoading, setIsCartLoading] = useState(true);
    const prevUserRef = useRef<User | null>(null);
    // Use a ref to access the current cart items inside useEffect without adding it to dependencies
    const cartItemsRef = useRef<CartItem[]>(cartItems);

    useEffect(() => {
        cartItemsRef.current = cartItems;
    }, [cartItems]);

    // Effect to LOAD and MIGRATE cart on auth state change
    useEffect(() => {
        if (authLoading) return;

        const loadAndSyncCart = async () => {
            setIsCartLoading(true);

            const prevUser = prevUserRef.current;
            const currentUser = user;

            // --- MIGRATION LOGIC: From Anonymous to Permanent User ---
            if (prevUser?.isAnonymous && currentUser && !currentUser.isAnonymous) {
                console.log('Migrating cart from anonymous to permanent user...');
                
                // 1. Get anonymous user's cart from LOCAL STATE (most up to date)
                // We use the Ref to ensure we get the items that were in memory just before the auth switch
                const localAnonItems = cartItemsRef.current;

                // 2. Get permanent user's existing cart from DATABASE
                const userCartRef = doc(db, 'carts', currentUser.uid);
                const userCartDoc = await getDoc(userCartRef);
                const serverItems: CartItem[] = userCartDoc.exists() ? userCartDoc.data().items : [];

                // 3. Merge carts. 
                // We take the server items (Product X) and append the local anonymous items (Product Y).
                const combinedCart = [...serverItems];
                const serverItemIds = new Set(serverItems.map(item => item.id));
                
                localAnonItems.forEach(anonItem => {
                    if (!serverItemIds.has(anonItem.id)) {
                        combinedCart.push(anonItem);
                    }
                });

                setCartItems(combinedCart);

                // 4. Save merged cart to permanent user's document
                if (combinedCart.length > 0) {
                    try {
                        const serializedCart = serializeCartItems(combinedCart);
                        await setDoc(userCartRef, { items: serializedCart });
                    } catch (error) {
                         console.error("Failed to persist migrated cart to Firestore:", error);
                    }
                }

                // 5. Delete old anonymous cart document to clean up
                const anonCartRef = doc(db, 'carts', prevUser.uid);
                await deleteDoc(anonCartRef).catch(() => {});
                console.log('Cart migration complete.');

            } 
            // --- NORMAL LOAD LOGIC ---
            else if (currentUser) {
                // This loads the cart for either an anonymous or a permanent user
                // ONLY if we didn't just migrate (migration handles its own setting)
                const cartRef = doc(db, 'carts', currentUser.uid);
                const cartDoc = await getDoc(cartRef);
                const serverCart: CartItem[] = cartDoc.exists() ? cartDoc.data().items : [];
                setCartItems(serverCart);
            } else {
                // No user (guest), so cart is empty.
                setCartItems([]);
            }
            setIsCartLoading(false);
        };

        loadAndSyncCart();
        
        // Update the ref for the next render
        prevUserRef.current = user;

    }, [user, authLoading]);


    // Effect for PERSISTING cart changes to Firestore
    useEffect(() => {
        if (isCartLoading || authLoading) return;

        const persistCart = async () => {
            if (user) { // Persist only if there's a user (anonymous or permanent)
                try {
                    const serializedCart = serializeCartItems(cartItems);

                    const cartRef = doc(db, 'carts', user.uid);
                    if (serializedCart.length > 0) {
                        await setDoc(cartRef, { items: serializedCart });
                    } else {
                        // If cart is empty, delete the Firestore document
                        await deleteDoc(cartRef).catch(() => {});
                    }
                } catch (error) {
                    console.error("Failed to persist cart to Firestore:", error);
                }
            }
        };

        // Debounce persistence to avoid excessive writes
        const timerId = setTimeout(persistCart, 500);
        return () => clearTimeout(timerId);

    }, [cartItems, user, isCartLoading, authLoading]);


    const addToCart = useCallback(async (item: CartItem, userId: string) => {
        const itemToAdd = { ...item };

        if (itemToAdd.designInfo?.files) {
            const persistedFiles: { [key: string]: PersistedFileInfo | null } = {};
            
            const uploadPath = `cart-uploads/${userId}/${item.id}`;
            
            await Promise.all(Object.entries(itemToAdd.designInfo.files).map(async ([key, fileInfo]) => {
                if (fileInfo?.file) {
                    try {
                        const url = await uploadFile(fileInfo.file, uploadPath, fileInfo.file.name);
                        persistedFiles[key] = {
                            name: fileInfo.file.name,
                            type: fileInfo.file.type,
                            size: fileInfo.file.size,
                            pdfPage: fileInfo.pdfPage,
                            rotation: fileInfo.rotation,
                            fit: fileInfo.fit,
                            orientation: fileInfo.orientation, // Persist orientation
                            dataUrl: url,
                        };
                    } catch (error) {
                        console.error(`Error uploading file ${fileInfo.file.name}:`, error);
                        throw new Error(`Failed to upload ${fileInfo.file.name}.`);
                    }
                }
            }));
            
            itemToAdd.designInfo.persistedFiles = persistedFiles;
        }
        
        setCartItems(prev => [...prev, itemToAdd]);
        trackAddToCart(itemToAdd);
    }, []);

    const removeFromCart = useCallback((itemId: string) => {
        setCartItems(prev => prev.filter(item => item.id !== itemId));
    }, []);

    const clearLocalCart = useCallback(() => {
        setCartItems([]);
    }, []);

    const updateItemQuantity = useCallback((itemId: string, newPriceInfo: QuantityPricePair) => {
        setCartItems(prev => prev.map(item => {
            if (item.id === itemId) {
                
                const oldQuantityDisplay = item.selectedOptions['Cantidad'] || String(item.selectedPriceInfo.quantity);
                const quantityParts = oldQuantityDisplay.split(' ');
                let newQuantityDisplay: string;
                
                if (quantityParts.length > 1 && isNaN(Number(quantityParts[1]))) {
                    const unit = quantityParts.slice(1).join(' ');
                    newQuantityDisplay = `${newPriceInfo.quantity} ${unit}`;
                } else {
                    newQuantityDisplay = String(newPriceInfo.quantity);
                }

                const newSelectedOptions = {
                    ...item.selectedOptions,
                    'Cantidad': newQuantityDisplay,
                    'Tiempo de Entrega': newPriceInfo.deliveryTime || 'N/A',
                };

                return {
                    ...item,
                    selectedPriceInfo: newPriceInfo,
                    selectedOptions: newSelectedOptions,
                };
            }
            return item;
        }));
    }, []);
    
    const toggleItemUrgency = useCallback((itemId: string) => {
        setCartItems(prev =>
            prev.map(item =>
                item.id === itemId
                    ? { ...item, isUrgencyActive: !item.isUrgencyActive }
                    : item
            )
        );
    }, []);

    const setUrgencyForAllItems = useCallback((isActive: boolean) => {
        setCartItems(prev =>
            prev.map(item => ({ ...item, isUrgencyActive: isActive }))
        );
    }, []);

    const totalItems = cartItems.length;

    const cartTotal = useMemo(() => {
        return cartItems.reduce((total, item) => {
            const basePrice = Number(item.selectedPriceInfo.price);
            const urgencyPrice = item.isUrgencyActive && item.selectedPriceInfo.urgencyPrice ? Number(item.selectedPriceInfo.urgencyPrice) : 0;
            const itemTotal = basePrice + urgencyPrice + item.designServicePrice;
            return total + itemTotal;
        }, 0);
    }, [cartItems]);


    const value = { cartItems, addToCart, removeFromCart, updateItemQuantity, toggleItemUrgency, setUrgencyForAllItems, clearLocalCart, totalItems, cartTotal, isCartLoading };

    return (
        <CartContext.Provider value={value}>
            {children}
        </CartContext.Provider>
    );
};

export default CartProvider;
